import { Fragment, useState, useEffect, useMemo } from "react";
import moment from "moment";
import { Container, Offcanvas, Badge, Form } from "react-bootstrap";
import { trellisPalette } from "components/custom/analytics/palettes";
import DatePicker from "react-datepicker";
import { Button } from "components/core/basic/Button";
import { HStack } from "../../../styled-system/jsx";
import { css } from "../../../styled-system/css";

const Calendar = () => {
  const [monthDay, setMonthDay] = useState(moment());
  const [firstDay, setFirstDay] = useState(monthDay.clone().startOf("month").startOf("week"));
  const [lastDay, setLastDay] = useState(monthDay.clone().endOf("month").endOf("week"));
  const [thisMonth, setThisMonth] = useState(monthDay.format("MMMM"));
  const [show, setShow] = useState(false);

  // For data
  const [eventName, setEventName] = useState("");
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [selectedEventType, setSelectedEventType] = useState("");

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const handleEventTypeClick = (eventType) => {
    setSelectedEventType(eventType);
  };

  const events = [
    {
      startDate: "2024-06-24",
      endDate: "2024-06-26",
      title: "Event 1",
      description: "Description 1",
      color: trellisPalette[2],
    },
    {
      startDate: "2024-06-24",
      endDate: "2024-07-02",
      title: "Event 2",
      description: "Description 1",
      color: trellisPalette[0],
    },
  ];

  useEffect(() => {
    setFirstDay(monthDay.clone().startOf("month").startOf("week"));
    setLastDay(monthDay.clone().endOf("month").endOf("week"));
    setThisMonth(monthDay.format("MMMM"));
  }, [monthDay]);

  const calendar = useMemo(() => {
    const calendar = [];

    const day = firstDay.clone().subtract(1, "day");

    while (day.isBefore(lastDay, "day")) {
      calendar.push(
        Array(7)
          .fill(0)
          .map(() => day.add(1, "day").clone()),
      );
    }

    return calendar;
  }, [firstDay, lastDay]);

  // Render the calendar in the UI
  return (
    <Container
      fluid
      style={{
        minHeight: "calc(100vh - 150px)",
        background: "#fefefe",
      }}
    >
      <HStack paddingX="medium" paddingY="small" justifyContent={"space-between"}>
        <HStack gap="large">
          <Button
            variant="secondary"
            onPress={() => {
              setMonthDay(moment());
            }}
          >
            Today
          </Button>
          <HStack gap="small">
            <Button
              variant="secondary"
              onPress={() => {
                setMonthDay(monthDay.clone().subtract(1, "month"));
              }}
            >
              &lt;
            </Button>
            <Button
              variant="secondary"
              onPress={() => {
                setMonthDay(monthDay.clone().add(1, "month"));
              }}
            >
              &gt;
            </Button>
          </HStack>
          <div className={css({ fontSize: "Large" })}>{monthDay.format("MMMM YYYY")}</div>{" "}
        </HStack>
        <Button onPress={handleShow} justifySelf="end">
          Add Event
        </Button>
      </HStack>

      <div className="calendar-grid">
        <div className="calendar-header">Sun</div>
        <div className="calendar-header">Mon</div>
        <div className="calendar-header">Tue</div>
        <div className="calendar-header">Wed</div>
        <div className="calendar-header">Thu</div>
        <div className="calendar-header">Fri</div>
        <div className="calendar-header">Sat</div>

        {calendar.map((week, i) => (
          <Fragment key={i}>
            {week.map((day, j) => (
              <div
                key={j}
                className="calendar-day"
                style={{ backgroundColor: day.format("MMMM") !== thisMonth ? "#F1E7EF" : null }}
              >
                <div className="calendar-day-number">{day.format("D")}</div>

                {events.map((event, k) => {
                  if (day.isBetween(event.startDate, event.endDate, null, "[]")) {
                    return (
                      <div key={k} className="calendar-event" style={{ bottom: 20 * k, backgroundColor: event.color }}>
                        <div className="calendar-event-title">
                          {event.title} - {k}
                        </div>
                      </div>
                    );
                  }
                })}
              </div>
            ))}
          </Fragment>
        ))}
      </div>
      <Offcanvas show={show} onHide={handleClose} placement="end">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title style={{ paddingBottom: 0, marginBottom: 0 }}>
            <div className="pt-2">Add Event</div>
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="fs-4" style={{ paddingTop: 0 }}>
          <div className="pt-4" style={{ borderTop: "1px solid #ccc" }}></div>
          <Form>
            <Form.Group className="mb-3" controlId="formEventName">
              <Form.Label>Event Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter event name"
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3 mt-3 mb-3" controlId="formStartDate">
              <Form.Label>Start Date</Form.Label>
              <span className="ps-3">
                <DatePicker selected={startDate} onChange={(date) => setStartDate(date)} className="form-control" />
              </span>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formEndDate">
              <Form.Label>End Date&nbsp;&nbsp;&nbsp;</Form.Label>
              <span className="ps-3">
                <DatePicker selected={endDate} onChange={(date) => setEndDate(date)} className="form-control" />
              </span>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Event Type</Form.Label>
              <div className="fs-4">
                <Badge
                  pill
                  bg={selectedEventType === "Pricing" ? "bright-pink" : "purple"}
                  onClick={() => handleEventTypeClick("Pricing")}
                  style={{ cursor: "pointer", margin: "0 5px" }}
                >
                  Pricing Event
                </Badge>
                <Badge
                  pill
                  bg={selectedEventType === "Advertising" ? "bright-pink" : "purple"}
                  onClick={() => handleEventTypeClick("Advertising")}
                  style={{ cursor: "pointer", margin: "0 5px" }}
                >
                  Advertising Event
                </Badge>
                <Badge
                  pill
                  bg={selectedEventType === "Coupon" ? "bright-pink" : "purple"}
                  onClick={() => handleEventTypeClick("Coupon")}
                  style={{ cursor: "pointer", margin: "0 5px" }}
                >
                  Coupon Event
                </Badge>
                <Badge
                  pill
                  bg={selectedEventType === "Promotional" ? "bright-pink" : "purple"}
                  onClick={() => handleEventTypeClick("Promotional")}
                  style={{ cursor: "pointer", margin: "0 5px" }}
                >
                  Promotional Event
                </Badge>
              </div>
            </Form.Group>

            <HStack justifyContent={"end"}>
              <Button
                onPress={() => {
                  //   setShowAddKWModal(true);
                }}
              >
                Add Event
              </Button>
            </HStack>
          </Form>
        </Offcanvas.Body>
      </Offcanvas>
    </Container>
  );
};

export default Calendar;
